import { useState, type ReactNode } from 'react';

import { ApiError } from '../app/api.js';
import { Button } from '../design-system/index.js';

/**
 * What a page shows when it throws.
 *
 * ── THE DEFAULT WAS "Something went wrong!" AND NOTHING ELSE ───────────────
 * That is TanStack Router's `CatchBoundary` fallback, and it is what a real user hit on a
 * real phone on 2026-08-09. It gave the operator nothing to report and gave me nothing to
 * diagnose — I spent a round of guesses reproducing it in jsdom, where it does not happen,
 * because there was no other information available.
 *
 * A crash that cannot be described is a crash that cannot be fixed, and on a mobile browser
 * the developer console is not realistically reachable. So this component's job is to turn an
 * exception into something a person can read aloud over the phone.
 *
 * ── WHAT IT SHOWS AND WHY EACH PART IS THERE ───────────────────────────────
 * · **The message, always visible.** Not behind a toggle. It is the single most useful string
 *   and it is usually short.
 * · **The stack, behind a disclosure.** Long, frightening, and exactly what identifies the
 *   line. `sourcemap: true` is set in `vite.config.ts` — deliberately, "a stack trace from a
 *   real registration failure on a real network is worth more than the few kB" — so these
 *   frames name real files rather than minified soup.
 * · **A copy button.** The realistic path is the operator copying this into a message. Asking
 *   somebody to retype a stack trace is asking them not to bother.
 * · **`requestId` when it is an `ApiError`.** That one string joins the browser to
 *   nginx's access log and to the API's own JSON log, which is the whole reason `http/app.ts`
 *   generates and echoes it.
 *
 * ── AND IT OFFERS A WAY OUT ────────────────────────────────────────────────
 * "Try again" re-renders; "Go to the dashboard" is a full navigation, which is the escape
 * hatch when the route itself is what is broken. A dead end with no controls is how a user
 * concludes the whole product is broken rather than one page.
 */

export interface RouteErrorProps {
  error: unknown;
  /** Provided by TanStack Router. Re-renders the route without a full page load. */
  reset?: () => void;
}

function describe(error: unknown): { name: string; message: string; stack?: string; requestId?: string } {
  if (error instanceof ApiError) {
    return {
      name: `${error.status} ${error.code}`,
      message: error.message,
      ...(error.stack ? { stack: error.stack } : {}),
      ...(error.requestId ? { requestId: error.requestId } : {}),
    };
  }
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      ...(error.stack ? { stack: error.stack } : {}),
    };
  }
  // A thrown string, or an object with no `message`. Rare, and precisely when the default
  // "Something went wrong" is least helpful.
  return { name: 'Error', message: typeof error === 'string' ? error : JSON.stringify(error) };
}

export function RouteError({ error, reset }: RouteErrorProps): ReactNode {
  const [copied, setCopied] = useState(false);
  const detail = describe(error);

  const report = [
    `${detail.name}: ${detail.message}`,
    detail.requestId ? `requestId: ${detail.requestId}` : '',
    `page: ${window.location.pathname}`,
    `agent: ${navigator.userAgent}`,
    detail.stack ?? '',
  ]
    .filter(Boolean)
    .join('\n');

  return (
    /*
      `role="alert"` so it is announced. A page that silently replaces its content with an
      error is, to a screen-reader user, a page that stopped existing.
    */
    <div role="alert" className="mx-auto max-w-2xl space-y-4 py-8">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-fg">This page did not load</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Something in the console failed rather than the switch — calls are unaffected.
        </p>
      </div>

      <div className="space-y-2 rounded-md border border-border bg-surface-sunken p-4">
        <p className="font-mono text-sm break-words text-danger">
          {detail.name}: {detail.message}
        </p>
        {detail.requestId ? (
          <p className="font-mono text-xs text-fg-muted">requestId: {detail.requestId}</p>
        ) : null}
        {detail.stack ? (
          <details>
            <summary className="cursor-pointer text-sm text-fg-muted">Technical detail</summary>
            {/* `overflow-x-auto` on its own container: a stack trace is the widest thing in the
                app and must not make the whole page scroll sideways. */}
            <pre className="mt-2 max-h-64 overflow-auto rounded bg-surface p-2 text-xs whitespace-pre-wrap text-fg-muted">
              {detail.stack}
            </pre>
          </details>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {reset ? (
          <Button variant="primary" onClick={reset}>
            Try again
          </Button>
        ) : null}
        <Button variant="secondary" asChild>
          <a href="/">Go to the dashboard</a>
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            navigator.clipboard
              .writeText(report)
              .then(() => setCopied(true))
              .catch(() => setCopied(false));
          }}
        >
          {copied ? 'Copied' : 'Copy details'}
        </Button>
        <span role="status" aria-live="polite" className="text-sm text-fg-muted">
          {copied ? 'Details copied — paste them into a message.' : ''}
        </span>
      </div>
    </div>
  );
}
